import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  AlertTriangle,
  CheckCircle,
  Eye,
  Search,
  Users,
  Clock,
  Archive,
  FileText,
  MessageSquare,
  X,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { ReportDetail } from "../components/ReportDetail";
import { adminApi } from "../utils/admin-api-axios";
import { useLocation } from "react-router-dom";

const Report = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [groupDuplicates, setGroupDuplicates] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const location = useLocation();
  const { chatId, autoOpenReport, filterType, reportId } = location.state || {};

  // Fetch reports on component mount and when filters change
  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [statusFilter, typeFilter, pagination.page, groupDuplicates]);

  // Handle navigation from chat detail - auto-open report
  useEffect(() => {
    if (autoOpenReport && reports.length > 0) {
      let targetReport = null;

      if (reportId) {
        // Find report by specific report ID
        targetReport = reports.find((report) => report.id === reportId);
      } else if (chatId) {
        // Find report related to the chat (existing logic)
        targetReport = reports.find(
          (report) =>
            report.type.toLowerCase() === "chat" &&
            (report.reportedChat === chatId || report.targetId === chatId)
        );
      }

      if (targetReport) {
        setSelectedReport(targetReport);
        setIsDetailOpen(true);
      }
    }
  }, [reports, chatId, autoOpenReport, reportId]);

  // Set initial filter if coming from chat
  useEffect(() => {
    if (filterType === "chat") {
      setTypeFilter("chat");
    }
  }, [filterType]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError("");

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        groupDuplicates: groupDuplicates.toString(),
      };

      if (statusFilter !== "all") params.status = statusFilter;
      if (typeFilter !== "all") params.type = typeFilter;

      const data = await adminApi.getAllReports(params);

      if (data.success) {
        let transformedReports;

        if (groupDuplicates) {
          // Handle grouped reports
          transformedReports = data.data.reports.map((group) => {
            const mainReport = group.reports[0];
            return {
              id: mainReport._id,
              reportedBy:
                group.reporters.length > 1
                  ? `${group.reporters.length} users`
                  : mainReport.reportedBy
                  ? `${mainReport.reportedBy.firstName} ${mainReport.reportedBy.lastName}`
                  : "Unknown User",
              reportedUser: mainReport.reportedUser
                ? `${mainReport.reportedUser.firstName} ${mainReport.reportedUser.lastName}`
                : "Unknown User",
              reportedListing:
                mainReport.type === "listing" ? mainReport.targetId : null,
              reportedChat:
                mainReport.type === "chat" ? mainReport.targetId : null,
              reportedUserId:
                mainReport.type === "user" ? mainReport.targetId : null,
              reason: mainReport.reason,
              description: mainReport.reason,
              date: new Date(group.latestReport).toISOString().split("T")[0],
              status: getDisplayStatus(group.statuses),
              type:
                mainReport.type.charAt(0).toUpperCase() +
                mainReport.type.slice(1),
              priority:
                group.reportCount > 3
                  ? "High"
                  : group.reportCount > 1
                  ? "Medium"
                  : "Low",
              adminNotes: mainReport.adminNotes || "",
              actionsTaken: mainReport.actionsTaken || [],
              originalStatus: mainReport.status,
              reportCount: group.reportCount,
              isGrouped: true,
              allReports: group.reports,
              target: group.target,
              targetId: mainReport.targetId,
              targetType: mainReport.type,
              hasAdminNotes: !!(
                mainReport.adminNotes && mainReport.adminNotes.trim()
              ),
              hasActions: !!(
                mainReport.actionsTaken && mainReport.actionsTaken.length > 0
              ),
            };
          });
        } else {
          // Handle individual reports
          transformedReports = data.data.reports.map((report) => ({
            id: report._id,
            reportedBy: report.reportedBy
              ? `${report.reportedBy.firstName} ${report.reportedBy.lastName}`
              : "Unknown User",
            reportedUser: report.reportedUser
              ? `${report.reportedUser.firstName} ${report.reportedUser.lastName}`
              : "Unknown User",
            reportedListing:
              report.type === "listing"
                ? typeof report.targetId === "object"
                  ? report.targetId._id
                  : report.targetId
                : null,
            reportedChat:
              report.type === "chat"
                ? typeof report.targetId === "object"
                  ? report.targetId._id
                  : report.targetId
                : null,
            reportedUserId:
              report.type === "user"
                ? typeof report.targetId === "object"
                  ? report.targetId._id
                  : report.targetId
                : null,
            reason: report.reason,
            description: report.reason,
            date: new Date(report.createdAt).toISOString().split("T")[0],
            status: getDisplayStatus([report.status]),
            type: report.type.charAt(0).toUpperCase() + report.type.slice(1),
            priority: "Medium",
            adminNotes: report.adminNotes || "",
            actionsTaken: report.actionsTaken || [],
            originalStatus: report.status,
            reportCount: 1,
            isGrouped: false,
            targetId:
              typeof report.targetId === "object"
                ? report.targetId._id
                : report.targetId,
            targetType: report.type,
            hasAdminNotes: !!(report.adminNotes && report.adminNotes.trim()),
            hasActions: !!(
              report.actionsTaken && report.actionsTaken.length > 0
            ),
          }));
        }

        setReports(transformedReports);
        setPagination((prev) => ({
          ...prev,
          total: data.data.pagination.total,
          pages: data.data.pagination.pages,
        }));
      } else {
        throw new Error(data.message || "Failed to fetch reports");
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      setError(error.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await adminApi.getReportStats();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const getDisplayStatus = (statuses) => {
    // Priority: pending > resolved > dismissed
    if (statuses.includes("pending")) return "Pending";
    if (statuses.includes("resolved")) return "Resolved";
    if (statuses.includes("dismissed")) return "Dismissed";
    return "Unknown";
  };

  const filteredReports = reports.filter((report) => {
    const matchesSearch =
      report.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.reportedBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.reportedUser.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (report.adminNotes &&
        report.adminNotes.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesSearch;
  });

  const handleBulkResolve = async (targetId, targetType) => {
    try {
      const data = await adminApi.bulkResolveReports({
        targetId,
        type: targetType,
        resolutionNote: "Bulk resolved by admin",
      });

      if (data.success) {
        await fetchReports();
        await fetchStats();
        console.log(`Bulk resolved ${data.data.resolvedCount} reports`);
      }
    } catch (error) {
      console.error("Error bulk resolving:", error);
      setError(error.message);
    }
  };

  const handleBulkDismiss = async (targetId, targetType) => {
    try {
      const data = await adminApi.bulkResolveReports({
        targetId,
        type: targetType,
        resolutionNote: "Bulk dismissed by admin",
        isDismissal: true,
      });

      if (data.success) {
        await fetchReports();
        await fetchStats();
        console.log(`Bulk dismissed ${data.data.resolvedCount} reports`);
      }
    } catch (error) {
      console.error("Error bulk dismissing:", error);
      setError(error.message);
    }
  };

  const handleDismissReport = async (reportId) => {
    try {
      const data = await adminApi.dismissReport(reportId, {
        adminNotes: "Dismissed by admin - no action needed",
        resolveAllDuplicates: false, // Set to false for individual dismiss
      });

      if (data.success) {
        await fetchReports();
        await fetchStats();
        console.log(`Report ${reportId} dismissed successfully`);
      } else {
        throw new Error(data.message || "Failed to dismiss report");
      }
    } catch (error) {
      console.error("Error dismissing report:", error);
      setError(error.message || "Failed to dismiss report");
    }
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setIsDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailOpen(false);
    setSelectedReport(null);
    // Clear the navigation state to prevent re-opening
    if (autoOpenReport) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const getAdminActivityIndicator = (report) => {
    const indicators = [];

    if (report.hasAdminNotes) {
      indicators.push(
        <div
          key="notes"
          className="flex items-center text-blue-600"
          title="Has admin notes"
        >
          <FileText className="w-3 h-3" />
        </div>
      );
    }

    if (report.hasActions) {
      indicators.push(
        <div
          key="actions"
          className="flex items-center text-red-600"
          title="Actions taken"
        >
          <CheckCircle className="w-3 h-3" />
        </div>
      );
    }

    return indicators.length > 0 ? (
      <div className="flex space-x-1">{indicators}</div>
    ) : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading reports...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 text-center">
          <p className="mb-2 font-semibold">Error Loading Reports</p>
          <p className="mb-4 text-sm">{error}</p>
          <button
            onClick={fetchReports}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Auto-open notification */}
      {autoOpenReport && (chatId || reportId) && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="!p-4">
            <div className="flex items-center space-x-2 text-blue-800">
              {chatId ? (
                <>
                  <MessageSquare className="w-5 h-5" />
                  <div>
                    <p className="font-medium">
                      Viewing reports for chat: {chatId}
                    </p>
                    <p className="text-sm">
                      Showing all reports related to this conversation. The
                      relevant report will open automatically if found.
                    </p>
                  </div>
                </>
              ) : reportId ? (
                <>
                  <FileText className="w-5 h-5" />
                  <div>
                    <p className="font-medium">
                      Opening specific report: {reportId.slice(-8)}
                    </p>
                    <p className="text-sm">
                      The selected report will open automatically if found.
                    </p>
                  </div>
                </>
              ) : null}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards - Updated with "Pending Reports" and "Dismissed" */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="!p-4">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                <div>
                  <p className="text-sm font-medium">Pending Reports</p>
                  <p className="text-2xl font-bold">
                    {stats.overview.pendingReports}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="!p-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-sm font-medium">Resolved</p>
                  <p className="text-2xl font-bold">
                    {stats.overview.resolvedReports}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="!p-4">
              <div className="flex items-center space-x-2">
                <X className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm font-medium">Dismissed</p>
                  <p className="text-2xl font-bold">
                    {stats.overview.dismissedReports}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="!p-4">
              <div className="flex items-center space-x-2">
                <Archive className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-sm font-medium">Total Reports</p>
                  <p className="text-2xl font-bold">
                    {stats.overview.totalReports}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Reports Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search reports by ID, user, reason, or admin notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Updated status filter options */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="listing">Listing</SelectItem>
                <SelectItem value="chat">Chat</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center space-x-2">
              <Switch
                checked={groupDuplicates}
                onCheckedChange={setGroupDuplicates}
              />
              <span className="text-sm">Group Duplicates</span>
            </div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Report ID</TableHead>
                  <TableHead>Reported By</TableHead>
                  <TableHead>Reported User/Item</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  {/* <TableHead>Priority</TableHead> */}
                  <TableHead>Admin Activity</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.length > 0 ? (
                  filteredReports.map((report) => (
                    <TableRow
                      key={report.id}
                      className={`hover:bg-gray-50 ${
                        chatId &&
                        (report.reportedChat === chatId ||
                          report.targetId === chatId)
                          ? "bg-blue-50 border-l-4 border-l-blue-500"
                          : ""
                      }`}
                    >
                      <TableCell className="font-mono text-sm">
                        {report.id.slice(-8)}
                        {report.isGrouped && report.reportCount > 1 && (
                          <Badge variant="secondary" className="ml-1 text-xs">
                            {report.reportCount}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          {report.reportCount > 1 && (
                            <Users className="w-4 h-4" />
                          )}
                          <span>{report.reportedBy}</span>
                        </div>
                      </TableCell>
                      <TableCell>{report.reportedUser}</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm">{report.reason}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{report.type}</Badge>
                      </TableCell>
                      <TableCell>{report.date}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            report.status === "Resolved"
                              ? "default"
                              : report.status === "Pending"
                              ? "destructive"
                              : report.status === "Dismissed"
                              ? "secondary"
                              : "secondary"
                          }
                        >
                          {report.status}
                        </Badge>
                      </TableCell>
                      {/* <TableCell>
                        <Badge
                          variant={
                            report.priority === "High"
                              ? "destructive"
                              : report.priority === "Medium"
                              ? "default"
                              : "secondary"
                          }
                        >
                          {report.priority}
                        </Badge>
                      </TableCell> */}
                      <TableCell>{getAdminActivityIndicator(report)}</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewReport(report)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>

                          {/* Updated button logic: Show Dismiss button for pending reports */}
                          {(report.status === "Pending" ||
                            report.originalStatus === "pending") && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDismissReport(report.id)}
                                title="Dismiss Report"
                              >
                                <X className="w-4 h-4" />
                              </Button>

                              {/* Separate bulk resolve and bulk dismiss buttons for grouped reports */}
                              {report.isGrouped && report.reportCount > 1 && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleBulkResolve(
                                        report.targetId,
                                        report.targetType
                                      )
                                    }
                                    title="Bulk resolve all reports for this target"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      handleBulkDismiss(
                                        report.targetId,
                                        report.targetType
                                      )
                                    }
                                    title="Bulk dismiss all reports for this target"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      className="text-center text-muted-foreground"
                    >
                      No reports found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
                of {pagination.total} {groupDuplicates ? "grouped " : ""}reports
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Previous
                </Button>
                <span className="px-3 py-1 text-sm">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Report Detail Modal */}
      <ReportDetail
        report={selectedReport}
        isOpen={isDetailOpen}
        onClose={handleCloseDetail}
        onResolve={handleDismissReport}
        onRefresh={fetchReports}
      />
    </div>
  );
};

export default Report;
